import React, {useMemo} from 'react';
import styled from 'styled-components';
import {useSelector} from 'react-redux';
import {IQuickFilter, IFilters} from '../redux/types';
import DropDownCheckBox from '../../../../stories/components/hotels/SRP/DropDownCheckBox/DropDownCheckBox';
import {ArrayTogglerBehaviorTypes} from '../../../hooks/useArrayToggler';
import QuickFiltersUI from '../../../../stories/components/hotels/SRP/QuickFiltersUI/QuickFiltersUI';
import {
  updateFilterForSingleSelection,
  getPopularFilterConfig,
  quickFilterNameMap,
} from '../utils/filter-utils';
import {isRnplFilterSelector} from '../redux/selectors';
import {pushga} from 'ga-utils';
import {SrpGAEvents} from '../../../config/ga-config';

const QuickFilters = ({
  data,
  updateFilterCaller,
  appliedFiltersInState,
}: {
  data: Array<IQuickFilter>;
  updateFilterCaller: Function;
  appliedFiltersInState: IFilters;
}) => {
  if (!data || !(data.length > 0)) return null;

  const isRnplFilter = useSelector(isRnplFilterSelector);

  const popularFilterConfig = useMemo(() => getPopularFilterConfig(isRnplFilter), [isRnplFilter]);

  const gaHandler = (action = '', label = '') => {
    pushga({
      event: 'HotelsDwebGeneric',
      eventCategory: SrpGAEvents.hotelSrpFilterClicked,
      eventAction: action,
      eventLabel: label,
    });
  };

  let singleOptionFilterList: Array<IQuickFilter> = [];

  const multiOptionFilterList = data.filter(item => {
    if (item.o && item.o.length > 1) return true;
    singleOptionFilterList.push(item);
  });

  const hasSingleOptionsFilter = singleOptionFilterList && singleOptionFilterList.length > 0;
  let singleOptionFilterDefaultState = [];
  if (hasSingleOptionsFilter) {
    Object.keys(appliedFiltersInState)
      .filter(
        item => popularFilterConfig.symbols.indexOf(item) > -1 && appliedFiltersInState[item] === 1,
      )
      .map(item => {
        let arg = {};
        arg[item] = appliedFiltersInState[item];
        singleOptionFilterDefaultState.push({n: popularFilterConfig.names[item], fd: {...arg}});
      });

    Object.keys(appliedFiltersInState)
      .filter(item => ['transit', 'location', 'landmark', 'location_search'].indexOf(item) > -1)
      .map(item => {
        appliedFiltersInState[item].map(subItem => singleOptionFilterDefaultState.push(subItem));
      });
  }

  return (
    <>
      {multiOptionFilterList.map(item => {
        const filterKey = Object.keys(item.fd)[0];
        return (
          <DropDownCheckBox
            title={item.n}
            data={item.o}
            lookup={item => item && item.fd && (item.fd.loc_id || item.fd.fid)}
            actionHandler={(items, itemUnderAction) => {
              let arg = {};
              arg[filterKey] = [
                ...items.map(item => {
                  if (item && item.fd) return {...item, ...item.fd};
                  return {...item};
                }),
              ];
              if (filterKey === 'landmark') arg['transit'] = [];
              else if (filterKey === 'transit') arg['landmark'] = [];
              updateFilterCaller(arg as IFilters);
              gaHandler(item.n, itemUnderAction?.n);
            }}
            resetHandlerProp={() => {
              let arg = {};
              if (filterKey === 'landmark' || filterKey === 'transit') {
                arg['landmark'] = [];
                arg['transit'] = [];
              } else arg[filterKey] = [];
              updateFilterCaller(arg as IFilters);
            }}
            renderItem={item => (
              <MultiOptionWrap>
                <MultiOptionSpan title={item.n}>{item.n}</MultiOptionSpan>
                {(item?.d && <MultiOptionDescriptionSpan>{item.d}</MultiOptionDescriptionSpan>) ||
                  null}
              </MultiOptionWrap>
            )}
            defaultState={appliedFiltersInState[filterKey]}
            width={'28rem'}
            type={
              item.m === 1
                ? ArrayTogglerBehaviorTypes.multiSelect
                : ArrayTogglerBehaviorTypes.singleSelect
            }
          />
        );
      })}

      {(hasSingleOptionsFilter && (
        <QuickFiltersUI
          data={singleOptionFilterList.filter(item =>
            item && item.fd && item.fd.utag ? false : true,
          )}
          lookup={item =>
            item && item.n && quickFilterNameMap[item.n]
              ? quickFilterNameMap[item.n]
              : item && item.n
          }
          actionHandler={(_, itemUnderAction, isSelected) => {
            const filterKey = Object.keys(itemUnderAction.fd)[0];
            if (Array.isArray(itemUnderAction.fd[filterKey])) {
              itemUnderAction.fd[filterKey] = itemUnderAction.fd[filterKey].map(item => ({
                ...item,
                n: itemUnderAction.n || '',
              }));
            }
            updateFilterCaller(
              updateFilterForSingleSelection(itemUnderAction.fd, appliedFiltersInState),
            );
            gaHandler(itemUnderAction?.n, isSelected);
          }}
          defaultState={singleOptionFilterDefaultState}
        />
      )) ||
        null}
    </>
  );
};

export default React.memo(QuickFilters);

const MultiOptionSpan = styled.span(
  props => `
  font-size: ${props.theme.font.custom(14)};
  cursor: pointer;
  max-width: 22rem;
  // white-space: nowrap;
  // overflow: hidden;
  // text-overflow: ellipsis;
`,
);

const MultiOptionDescriptionSpan = styled.div(
  props => `
  color: ${props.theme.colors.downriver.medium};
  margin-top: ${props.theme.spacing.small};
  cursor: pointer;
  max-width: 21rem;
  flex-wrap:wrap;
  display: -webkit-box;
  line-clamp: 2;
  box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`,
);

const MultiOptionWrap = styled.div`
  display: flex;
  flex-direction: column;
`;
